import React from "react";
import moment from "moment";
import * as Icon from "react-feather";

import "./statement-verification-modal.css";

import BookkeepingService from "../../../../services/BookkeepingService";
import UtilityService from "../../../../services/UtilityService";

import WComponent from "../../../common/WComponent";
import ToolsModal from "../kit/ToolsModal";
import StatementEntryModal from "./StatementEntryModal";

class StatementVerificationModal extends WComponent {
	constructor(props) {
		super(props);

		this.parseQueue = props.groups.flatMap((group, groupIndex) => group.files.map(file => ({ groupIndex, file })));
		this.isPumping = false;
		this.cancelled = false;
		this.filesByKey = {};
		for (const queued of this.parseQueue) this.rememberFile(queued);

		this.state = {
			parsesByGroup: props.groups.map(() => []),
			fileTotals: props.groups.map(group => group.files.length),
			currentFile: null,
			pendingCount: this.parseQueue.length,
			enteringForGroup: null,
			enteringForFileName: null,
			enteringWhy: "",
			enteringPrefill: null,
		};
	}

	rememberFile = ({ groupIndex, file }) => {
		this.filesByKey[`${groupIndex}|${file.name}`] = file;
	};

	componentDidMount = async () => {
		await this.pumpParseQueue();
	};

	componentWillUnmount() {
		this.cancelled = true;
	}

	pumpParseQueue = async () => {
		if (this.isPumping) return;
		this.isPumping = true;

		const { clientId } = this.props;
		while (this.parseQueue.length > 0) {
			if (this.cancelled) return;
			const { groupIndex, file } = this.parseQueue.shift();
			await this.update({ currentFile: { groupIndex, fileName: file.name } });

			const parse = file.documentId
				? await BookkeepingService.rereadStatementFile({ clientId, documentId: file.documentId })
				: await BookkeepingService.parseBankStatementPdf({ clientId, file });
			const parseWithFileName = { fileName: file.name, status: "failed", ...parse };

			this.setState(prev => {
				const parsesByGroup = prev.parsesByGroup.map((groupParses, index) => (index === groupIndex ? [...groupParses, parseWithFileName] : groupParses));
				return { parsesByGroup, pendingCount: prev.pendingCount - 1 };
			});
		}

		this.isPumping = false;
		await this.update({ currentFile: null });
	};

	onAddFilesToGroup = (groupIndex, event) => {
		const files = Array.from(event.target.files || []);
		event.target.value = "";
		if (files.length === 0) return;

		const queued = files.map(file => ({ groupIndex, file }));
		for (const entry of queued) this.rememberFile(entry);

		this.parseQueue.push(...queued);
		this.setState(prev => ({
			fileTotals: prev.fileTotals.map((total, index) => (index === groupIndex ? total + files.length : total)),
			pendingCount: prev.pendingCount + files.length,
		}));
		this.pumpParseQueue();
	};

	onTryAgain = ({ groupIndex, fileName }) => {
		const file = this.filesByKey[`${groupIndex}|${fileName}`];
		if (!file) return;

		this.parseQueue.push({ groupIndex, file });
		this.setState(
			prev => ({
				parsesByGroup: prev.parsesByGroup.map((parses, index) => (index === groupIndex ? parses.filter(parse => parse.fileName !== fileName) : parses)),
				pendingCount: prev.pendingCount + 1,
			}),
			this.pumpParseQueue
		);
	};

	onEnterByHand = ({ groupIndex, fileName, why }) => {
		this.update({ enteringForGroup: groupIndex, enteringForFileName: fileName, enteringWhy: why || "", enteringPrefill: null });
	};

	onEnterTheMissingMonth = ({ groupIndex, missing }) => {
		this.update({
			enteringForGroup: groupIndex,
			enteringForFileName: null,
			enteringWhy: "No statement on file covers this period, so the balance jumps across the join.",
			enteringPrefill: missing,
		});
	};

	onEntryClosed = () => this.update({ enteringForGroup: null, enteringForFileName: null, enteringWhy: "", enteringPrefill: null });

	onEntryFiled = ({ parse }) => {
		const groupIndex = this.state.enteringForGroup;
		const replacedFileName = this.state.enteringForFileName;

		this.setState(prev => ({
			parsesByGroup: prev.parsesByGroup.map((parses, index) =>
				index === groupIndex ? [...parses.filter(existing => existing.fileName !== replacedFileName), parse] : parses
			),
			enteringForGroup: null,
			enteringForFileName: null,
			enteringWhy: "",
		}));
	};

	isProcessing = () => this.state.pendingCount > 0;

	getGroupReports = () => {
		const { fiscalPeriod } = this.props;
		const reports = this.state.parsesByGroup.map(parses => BookkeepingService.buildStatementRunReport({ parses, fiscalPeriod }));
		return reports;
	};

	buildVerifiedTransactions = () => {
		const { groups } = this.props;
		const transactions = [];

		this.state.parsesByGroup.forEach((parses, groupIndex) => {
			const assignedAccountName = groups[groupIndex].accountName;
			parses.forEach(parse => {
				if (parse.status !== "ok" && parse.status !== "partial") return;
				const statementAccountLabel = [parse.bankName, parse.accountNumberMasked].filter(Boolean).join(" ");
				const bankAccountName = assignedAccountName || statementAccountLabel || UtilityService.getFileNameWithoutExtension(parse.fileName);
				(parse.transactions || []).forEach(transaction => {
					transactions.push({
						date: transaction.date,
						description: transaction.description,
						amount: transaction.amount,
						bankAccountName,
						statementFileHash: parse.fileHash || null,
					});
				});
			});
		});

		return transactions;
	};

	onImportClicked = () => {
		const { onComplete, groups } = this.props;
		const transactions = this.buildVerifiedTransactions();
		const reports = this.getGroupReports();
		const fileNames = this.state.parsesByGroup.flatMap(parses => parses.map(parse => parse.fileName));
		const parsesWithAccounts = this.state.parsesByGroup.flatMap((parses, groupIndex) =>
			parses.map(parse => ({ parse, accountLabel: groups[groupIndex].accountName || null }))
		);
		onComplete({ transactions, reports, fileNames, parsesWithAccounts });
	};

	buildPeriodLabel = statement => {
		if (!statement.periodStart || !statement.periodEnd) return "Period unknown";
		const start = moment(statement.periodStart, "YYYY-MM-DD");
		const end = moment(statement.periodEnd, "YYYY-MM-DD");
		if (!start.isValid() || !end.isValid()) return "Period unknown";
		const sameYear = start.year() === end.year();
		const startLabel = start.format(sameYear ? "MMM D" : "MMM D, YYYY");
		return `${startLabel} – ${end.format("MMM D, YYYY")}`;
	};

	buildYearCorrectionTitle = statement => {
		const corrections = statement.yearCorrections || [];
		const movedLines = corrections.map(correction => `${correction.from} → ${correction.to}  ${correction.description}`);
		const preamble = "This statement prints dates without a year, so these were re-yeared from the statement's own period:";
		return [preamble, ...movedLines].join("\n");
	};

	renderCheckCell = ({ ok, failText, pendingText }) => {
		if (ok === null || ok === undefined) {
			return <span className="sv-modal__check sv-modal__check--muted">{pendingText || ""}</span>;
		}
		if (ok) return <span className="sv-modal__check sv-modal__check--pass"><Icon.Check size={13} /></span>;
		return <span className="sv-modal__check sv-modal__check--fail"><Icon.AlertTriangle size={12} /> {failText}</span>;
	};

	renderStatementRow = (statement, index, groupIndex) => {
		if (!statement.parsedOk) {
			const why = statement.errorDetail || "Could not be read";
			const canTryAgain = !!this.filesByKey[`${groupIndex}|${statement.fileName}`];

			return (
				<div key={`${statement.fileName}-${index}`} className="sv-modal__failed">
					<span className="sv-modal__failed-name">
						<Icon.XCircle size={14} className="sv-modal__line-fail-icon" />
						{statement.fileName}
					</span>
					<p className="sv-modal__failed-why" title={why}>{why}</p>
					<span className="sv-modal__failed-fixes">
						{canTryAgain && (
							<button type="button" className="tk-text-action" onClick={() => this.onTryAgain({ groupIndex, fileName: statement.fileName })}>
								Try again
							</button>
						)}
						<button
							type="button"
							className="tk-text-action tk-text-action--muted"
							onClick={() => this.onEnterByHand({ groupIndex, fileName: statement.fileName, why })}
						>
							Enter it by hand
						</button>
					</span>
				</div>
			);
		}

		return (
			<div key={`${statement.fileName}-${index}`} className="sv-modal__line" title={statement.fileName}>
				<span className="sv-modal__line-period">
					<span className="sv-modal__line-period-label">{this.buildPeriodLabel(statement)}</span>
					{statement.statementType === "credit_card" && <span className="sv-modal__type-chip">Credit card</span>}
					{statement.fromRegistry && (
						<span className="sv-modal__type-chip" title="This exact file is already on this client's record — the stored data was reused instead of re-reading the PDF">
							Already on file
						</span>
					)}
					{statement.enteredByHand && (
						<span className="sv-modal__type-chip sv-modal__type-chip--warning" title="These figures were entered by a person rather than read off the statement">
							Entered by hand
						</span>
					)}
					{statement.isWhollyOutsidePeriod && (
						<span className="sv-modal__type-chip sv-modal__type-chip--danger" title="Every transaction on this statement is dated outside the fiscal period — it looks like the wrong year">
							Wrong year
						</span>
					)}
					{!statement.isWhollyOutsidePeriod && statement.outOfPeriodCount > 0 && (
						<span className="sv-modal__type-chip sv-modal__type-chip--warning" title="These transactions fall outside the fiscal period and will be held out of the books at generation">
							{statement.outOfPeriodCount} outside period
						</span>
					)}
					{statement.yearsCorrectedCount > 0 && (
						<span className="sv-modal__type-chip sv-modal__type-chip--warning" title={this.buildYearCorrectionTitle(statement)}>
							{statement.yearsCorrectedCount} date{statement.yearsCorrectedCount === 1 ? "" : "s"} re-yeared
						</span>
					)}
				</span>
				<span className="sv-modal__line-balances">
					{UtilityService.formatCurrency(statement.openingBalance)}
					<Icon.ArrowRight size={11} className="sv-modal__line-arrow" />
					{UtilityService.formatCurrency(statement.closingBalance)}
				</span>
				<span className="sv-modal__line-count">{statement.transactionCount}</span>
				{this.renderCheckCell({
					ok: statement.tieOk,
					failText: `off ${UtilityService.formatCurrency(Math.abs(statement.tieDelta || 0))}`,
				})}
				{this.renderCheckCell({
					ok: statement.continuityOk,
					failText: `gap ${UtilityService.formatCurrency(Math.abs(statement.continuityDelta || 0))}`,
					pendingText: "—",
				})}
			</div>
		);
	};

	renderTheMonthThatIsMissing = ({ statement, groupIndex }) => {
		const missing = statement.theMonthThatIsMissing;
		if (!missing) return null;

		const from = missing.periodStart ? moment(missing.periodStart, "YYYY-MM-DD").format("MMM D, YYYY") : "the previous statement";
		const to = missing.periodEnd ? moment(missing.periodEnd, "YYYY-MM-DD").format("MMM D, YYYY") : "this one";

		return (
			<div key={`${statement.fileName}-gap`} className="sv-modal__gap">
				<p className="sv-modal__gap-why">
					<Icon.AlertTriangle size={13} className="sv-modal__gap-icon" />
					Nothing on file covers <strong>{from} – {to}</strong>, and the balance jumps{" "}
					<strong>{UtilityService.formatCurrency(Math.abs(missing.amount))}</strong> across the join.
				</p>
				<span className="sv-modal__gap-fixes">
					<label className="tk-text-action sv-modal__gap-add">
						Add the missing statement
						<input type="file" accept=".pdf" multiple className="sv-modal__gap-input" onChange={event => this.onAddFilesToGroup(groupIndex, event)} />
					</label>
					<button
						type="button"
						className="tk-text-action tk-text-action--muted"
						onClick={() => this.onEnterTheMissingMonth({ groupIndex, missing })}
					>
						Enter it by hand
					</button>
				</span>
			</div>
		);
	};

	renderColumnHeadings = () => (
		<div className="sv-modal__line sv-modal__line--headings">
			<span className="sv-modal__line-period">Period</span>
			<span className="sv-modal__line-balances">Opening → closing</span>
			<span className="sv-modal__line-count" title="How many transactions were read from this statement">Transactions</span>
			<span className="sv-modal__check-heading" title="Opening balance plus every transaction equals the closing balance printed on the statement">Adds up</span>
			<span className="sv-modal__check-heading" title="This statement opens exactly where the previous one closed — no missing or duplicated month">Follows prior</span>
		</div>
	);

	renderGroup = (group, groupIndex) => {
		const report = this.getGroupReports()[groupIndex];
		const { currentFile, fileTotals } = this.state;
		const parsingHere = currentFile && currentFile.groupIndex === groupIndex;
		const accountLabel = group.accountName || report.accountNumbersSeen.join(", ") || "Statements";
		const hasMixedAccounts = report.accountNumbersSeen.length > 1;

		return (
			<div key={groupIndex} className="sv-modal__group">
				<div className="sv-modal__group-head">
					<span className="sv-modal__group-name">{accountLabel}</span>
					<span className="sv-modal__group-count">{report.statements.length} of {fileTotals[groupIndex]} checked</span>
				</div>
				{hasMixedAccounts && (
					<div className="sv-modal__mixed-warning">
						<Icon.AlertTriangle size={13} />
						More than one card number appears here ({report.accountNumbersSeen.join(", ")}). That is normal for a supplementary card on the same account — check they belong together before importing.
					</div>
				)}
				<div className="sv-modal__table">
					{this.renderColumnHeadings()}
					{report.statements.map((statement, index) => [
						this.renderTheMonthThatIsMissing({ statement, groupIndex }),
						this.renderStatementRow(statement, index, groupIndex),
					])}
					{parsingHere && (
						<div className="sv-modal__line sv-modal__line--parsing">
							<Icon.Loader size={13} className="sv-modal__spinner" />
							<span className="sv-modal__line-period sv-modal__line-period--file">{currentFile.fileName}</span>
							<span className="sv-modal__line-parsing-note">Extracting and checking…</span>
						</div>
					)}
					<label className="sv-modal__add-row">
						<Icon.Plus size={13} />
						Add statement — for a missing month or a corrected file
						<input type="file" accept=".pdf" multiple className="sv-modal__add-input" onChange={event => this.onAddFilesToGroup(groupIndex, event)} />
					</label>
				</div>
			</div>
		);
	};

	buildCoverageGapMessages = reports => {
		const messages = [];
		reports.forEach((report, groupIndex) => {
			const coverage = report.periodCoverage;
			if (!coverage) return;
			const accountLabel = this.props.groups[groupIndex].accountName || report.accountNumbersSeen.join(", ") || `group ${groupIndex + 1}`;
			if (coverage.missingAtStart) {
				messages.push(`${accountLabel}: statements only begin ${moment(coverage.statementsStart).format("MMM D, YYYY")} — the start of the fiscal year is not covered`);
			}
			if (coverage.missingAtEnd) {
				messages.push(`${accountLabel}: statements stop ${moment(coverage.statementsEnd).format("MMM D, YYYY")} — the end of the fiscal year is not covered`);
			}
		});
		return messages;
	};

	renderSummary = () => {
		if (this.isProcessing()) return null;

		const reports = this.getGroupReports();
		const totalTransactions = reports.reduce((total, report) => total + report.totalTransactions, 0);
		const failedCount = reports.reduce((total, report) => total + report.statements.filter(statement => !statement.parsedOk).length, 0);
		const untiedCount = reports.reduce((total, report) => total + report.statements.filter(statement => statement.parsedOk && !statement.tieOk).length, 0);
		const breakCount = reports.reduce((total, report) => total + report.breakCount, 0);
		const wrongYearCount = reports.reduce((total, report) => total + report.statements.filter(statement => statement.isWhollyOutsidePeriod).length, 0);
		const totalOutOfPeriod = reports.reduce((total, report) => total + (report.totalOutOfPeriod || 0), 0);
		const coverageGaps = this.buildCoverageGapMessages(reports);
		const isClean = failedCount === 0 && untiedCount === 0 && breakCount === 0 && wrongYearCount === 0 && coverageGaps.length === 0;

		if (isClean) {
			return (
				<div className="sv-modal__summary sv-modal__summary--clean">
					<Icon.CheckCircle size={15} />
					Every statement ties to its printed closing balance and each month opens exactly where the previous one closed. {totalTransactions} transactions ready to import.
					{totalOutOfPeriod > 0 && ` ${totalOutOfPeriod} of them are dated outside the fiscal period and will be held out of the books.`}
				</div>
			);
		}

		const problems = [];
		if (failedCount > 0) problems.push(`${failedCount} statement${failedCount === 1 ? "" : "s"} could not be parsed`);
		if (untiedCount > 0) problems.push(`${untiedCount} do${untiedCount === 1 ? "es" : ""} not tie to the printed closing balance`);
		if (breakCount > 0) problems.push(`${breakCount} month-to-month break${breakCount === 1 ? "" : "s"} — a statement is likely missing or out of order`);
		if (wrongYearCount > 0) problems.push(`${wrongYearCount} statement${wrongYearCount === 1 ? " is" : "s are"} entirely outside the fiscal period — likely the wrong year`);
		problems.push(...coverageGaps);
		if (totalOutOfPeriod > 0) problems.push(`${totalOutOfPeriod} transaction${totalOutOfPeriod === 1 ? "" : "s"} dated outside the fiscal period will be held out of the books`);

		return (
			<div className="sv-modal__summary sv-modal__summary--problems">
				<Icon.AlertTriangle size={15} />
				{problems.join("; ")}. A statement that could not be read can be tried again, or entered by hand from its own row. Use “Add statement” under the account to drop in a missing month — the checks re-run as it lands. You can also import as-is.
			</div>
		);
	};

	renderFooter = () => {
		const { onClose } = this.props;
		const processing = this.isProcessing();

		const totalTransactions = processing ? 0 : this.getGroupReports().reduce((total, report) => total + report.totalTransactions, 0);

		return (
			<>
				<span />
				<span className="tk-modal-actions">
					<button type="button" className="tk-button tk-button--secondary" onClick={onClose}>Cancel</button>
					<button type="button" className="tk-button tk-button--primary" onClick={this.onImportClicked} disabled={processing || totalTransactions === 0}>
						<Icon.Check size={16} />
						{processing ? "Checking…" : `Import ${totalTransactions} transactions`}
					</button>
				</span>
			</>
		);
	};

	renderEntryModal = () => {
		const { clientId, groups, accountsAStatementCouldBelongTo = [] } = this.props;
		const { enteringForGroup, enteringForFileName, enteringWhy, enteringPrefill } = this.state;
		if (enteringForGroup === null) return null;

		const accountOptions = accountsAStatementCouldBelongTo.map(accountName => ({ value: accountName, label: accountName }));

		return (
			<StatementEntryModal
				clientId={clientId}
				accountOptions={accountOptions}
				accountLabel={groups[enteringForGroup] ? groups[enteringForGroup].accountName : ""}
				stalledFile={enteringForFileName ? { fileName: enteringForFileName, whyItStalled: enteringWhy } : null}
				theMonthThatIsMissing={enteringPrefill}
				onFiled={this.onEntryFiled}
				onClose={this.onEntryClosed}
			/>
		);
	};

	render() {
		const { groups, onClose } = this.props;

		return (
			<>
				<ToolsModal open={true} onClose={onClose} title="Verifying bank statements" width={720} footer={this.renderFooter()}>
					<p className="sv-modal__intro">
						Each statement is read, its amounts are re-derived from the bank's own running balance, and its opening and closing are checked against the statement before anything is imported.
					</p>
					{groups.map(this.renderGroup)}
					{this.renderSummary()}
				</ToolsModal>
				{this.renderEntryModal()}
			</>
		);
	}
}

export default StatementVerificationModal;
